/**
 * Turn `deck.details.synergy.list` into a graph ready for force-directed
 * layout. Nodes = participating cards (ranked + trimmed to the top N by
 * score), edges = co-occurrences of one card appearing as the source /
 * enabler of another card's entry, weighted by how many times the pair
 * shows up.
 *
 * The heavy trimming is deliberate — a real deck can surface 40+ cards
 * and hundreds of edges in its synergy list, which produces a visually
 * useless hairball. Keeping it to the top ~25 scoring cards makes the
 * web readable and still captures the load-bearing structure of the plan.
 */

import { SYNERGY_CATEGORY_ORDER } from './synergyCategoryConstants';

const DEFAULT_NODE_LIMIT = 25;

function extractId(candidate) {
    if (!candidate) return null;
    if (typeof candidate === 'string') return candidate;
    return candidate.id || candidate.cardId || null;
}

function dominantCategory(catCounts) {
    let best = null;
    let bestCount = -1;
    Object.entries(catCounts || {}).forEach(([cat, count]) => {
        if (count > bestCount) {
            best = cat;
            bestCount = count;
        }
    });
    return best;
}

export function buildSynergyGraph(synergyList, options = {}) {
    const nodeLimit = options.nodeLimit || DEFAULT_NODE_LIMIT;
    if (!synergyList || typeof synergyList !== 'object') {
        return { nodes: [], edges: [] };
    }

    const nodeMap = new Map();
    const edgeMap = new Map();

    const touchNode = (id) => {
        if (!id) return null;
        if (!nodeMap.has(id)) {
            nodeMap.set(id, {
                id,
                score: 0,
                entryCount: 0,
                refCount: 0,
                catCounts: {},
            });
        }
        return nodeMap.get(id);
    };

    const recordEdge = (ownerId, otherId, weight = 1) => {
        if (!ownerId || !otherId || ownerId === otherId) return;
        const [a, b] = ownerId < otherId ? [ownerId, otherId] : [otherId, ownerId];
        const key = `${a}\u0001${b}`;
        if (!edgeMap.has(key)) {
            edgeMap.set(key, { source: a, target: b, weight: 0 });
        }
        edgeMap.get(key).weight += weight;
    };

    Object.keys(synergyList).forEach((ownerId) => {
        const node = synergyList[ownerId];
        if (!node) return;
        const owner = touchNode(ownerId);
        SYNERGY_CATEGORY_ORDER.forEach((cat) => {
            const bucket = node[cat] || {};
            Object.keys(bucket).forEach((entryKey) => {
                const entry = bucket[entryKey];
                if (!entry) return;
                owner.entryCount += 1;
                owner.catCounts[cat] = (owner.catCounts[cat] || 0) + 1;
                const entryScore = Number(entry?.conditionScoring?.total || 0);
                owner.score += entryScore;

                const addEdgesFrom = (list) => {
                    if (!Array.isArray(list)) return;
                    list.forEach((src) => {
                        const otherId = extractId(src);
                        if (!otherId) return;
                        const other = touchNode(otherId);
                        if (otherId !== ownerId) {
                            other.refCount += 1;
                        }
                        recordEdge(ownerId, otherId, 1);
                    });
                };

                addEdgesFrom(entry.sources);
                addEdgesFrom(entry.enablers);
            });
        });
    });

    // rank and trim
    const ranked = Array.from(nodeMap.values())
        .sort((a, b) => b.score - a.score || b.refCount - a.refCount)
        .slice(0, nodeLimit);
    const keepIds = new Set(ranked.map((n) => n.id));

    const nodes = ranked.map((n) => ({
        id: n.id,
        score: n.score,
        entryCount: n.entryCount,
        refCount: n.refCount,
        category: dominantCategory(n.catCounts),
    }));

    const edges = [];
    edgeMap.forEach((e) => {
        if (keepIds.has(e.source) && keepIds.has(e.target)) edges.push(e);
    });

    return { nodes, edges };
}
